/**
 * Plan-Feature-Konfiguration für medizzconnect™.
 *
 * Definiert die 3 Abo-Stufen (BASIS, STANDARD, PREMIUM) und welche
 * Features jedem Plan zur Verfügung stehen. Admin kann diese Werte
 * später über ein Admin-Panel in der DB überschreiben.
 *
 * Nutzung:
 *   import { getPlanFeatures, canAccess } from '@medizzconnect/shared/plan-features';
 *   const features = getPlanFeatures('premium');
 *   if (canAccess('premium', 'socialMedia')) { ... }
 */

// ── Plan Typen ──

export type PlanId = 'basis' | 'standard' | 'premium';

export interface PlanFeatures {
  /** Plan-Bezeichnung */
  id: PlanId;
  label: string;
  /** Monatlicher Preis in Euro */
  priceMonthly: number;
  /** Jährlicher Preis in Euro (pro Monat) */
  priceYearly: number;
  /** Video-Typ: 'reel' (kurz) oder 'video' (lang) */
  videoType: 'reel' | 'video';
  /** Maximale Video-Dauer in Sekunden */
  maxVideoDuration: number;
  /** Kurzbeschreibung / Tätigkeitsfeld */
  hasDescription: boolean;
  /** Logo in Beschreibung anzeigen */
  hasLogo: boolean;
  /** Hookline (Spezialgebiet / Uni / Motto) */
  hasHookline: boolean;
  /** Maximale Anzahl Behandlungsangebote */
  maxTreatments: number;
  /** Kontaktfeld aktiv (klickbar) vs. nur Text */
  activeContact: boolean;
  /** Kontakt: E-Mail */
  hasEmail: boolean;
  /** Kontakt: Telefon */
  hasPhone: boolean;
  /** Kontakt: WhatsApp */
  hasWhatsApp: boolean;
  /** Versicherteninfo */
  hasInsuranceInfo: boolean;
  /** Praxisinformationen */
  hasPracticeInfo: boolean;
  /** Social-Media-Links (Instagram, Facebook, etc.) */
  hasSocialMedia: boolean;
  /** Premium-Badge auf der Video-Karte */
  hasPremiumBadge: boolean;
  /** Download-GIF für Praxis-Korrespondenz */
  hasDownloadGif: boolean;
  /** Öffnungszeiten */
  hasOpeningHours: boolean;
  /** Standort-Karte */
  hasLocationMap: boolean;
  /** Klinik-Team Sektion */
  hasClinicTeam: boolean;
  /** Website-Link */
  hasWebsite: boolean;
  /** Booking-URL */
  hasBookingUrl: boolean;
}

// ── Standard Plan-Definitionen ──

export const PLAN_FEATURES: Record<PlanId, PlanFeatures> = {
  basis: {
    id: 'basis',
    label: 'BASIS',
    priceMonthly: 0,
    priceYearly: 0,
    videoType: 'reel',
    maxVideoDuration: 15,
    hasDescription: true,
    hasLogo: false,
    hasHookline: true,
    maxTreatments: 2,
    activeContact: false,
    hasEmail: true,
    hasPhone: true,
    hasWhatsApp: true,
    hasInsuranceInfo: true,
    hasPracticeInfo: true,
    hasSocialMedia: false,
    hasPremiumBadge: false,
    hasDownloadGif: false,
    hasOpeningHours: false,
    hasLocationMap: false,
    hasClinicTeam: false,
    hasWebsite: false,
    hasBookingUrl: false,
  },

  standard: {
    id: 'standard',
    label: 'STANDARD',
    priceMonthly: 48,
    priceYearly: 40,
    videoType: 'video',
    maxVideoDuration: 40,
    hasDescription: true,
    hasLogo: true,
    hasHookline: true,
    maxTreatments: 4,
    activeContact: true,
    hasEmail: true,
    hasPhone: true,
    hasWhatsApp: true,
    hasInsuranceInfo: true,
    hasPracticeInfo: true,
    hasSocialMedia: true,
    hasPremiumBadge: false,
    hasDownloadGif: true,
    hasOpeningHours: true,
    hasLocationMap: false,
    hasClinicTeam: false,
    hasWebsite: true,
    hasBookingUrl: true,
  },

  premium: {
    id: 'premium',
    label: 'PREMIUM',
    priceMonthly: 64,
    priceYearly: 54,
    videoType: 'video',
    maxVideoDuration: 80,
    hasDescription: true,
    hasLogo: true,
    hasHookline: true,
    maxTreatments: 8,
    activeContact: true,
    hasEmail: true,
    hasPhone: true,
    hasWhatsApp: true,
    hasInsuranceInfo: true,
    hasPracticeInfo: true,
    hasSocialMedia: true,
    hasPremiumBadge: true,
    hasDownloadGif: true,
    hasOpeningHours: true,
    hasLocationMap: true,
    hasClinicTeam: true,
    hasWebsite: true,
    hasBookingUrl: true,
  },
} as const;

// ── Addon-Definitionen ──

/** Bekannte Addon-Keys */
export type AddonKey = 'APPOINTMENT_LINK' | 'PRIORITY_RANKING';

/** Welche Plan-Features ein Addon freischaltet (Overrides) */
const ADDON_FEATURE_OVERRIDES: Record<AddonKey, Partial<PlanFeatures>> = {
  APPOINTMENT_LINK: {
    hasBookingUrl: true,
  },
  PRIORITY_RANKING: {
    // Priority-Ranking hat keinen direkten Feature-Override,
    // sondern wirkt auf die Sortierung im Video-Grid.
    // Wir setzen trotzdem ein Flag für Transparenz.
  },
};

const KNOWN_ADDONS = new Set<string>(Object.keys(ADDON_FEATURE_OVERRIDES));

// ── Hilfsfunktionen ──

/** Plan-String auf PlanId normalisieren */
export function normalizePlanId(plan?: string | null): PlanId {
  if (!plan) return 'basis';
  const lower = plan.toLowerCase().trim();
  if (lower === 'premium') return 'premium';
  if (lower === 'standard') return 'standard';
  // BASIS, BASIC, FREE, STARTER → alles 'basis'
  if (lower === 'basis' || lower === 'basic' || lower === 'free' || lower === 'starter') return 'basis';
  return 'basis';
}

/**
 * Features für gegebenen Plan holen, optional mit Addon-Overrides.
 * @param plan - Plan-Name (BASIS, STANDARD, PREMIUM)
 * @param addons - Aktive Addon-Keys (z.B. ['APPOINTMENT_LINK', 'PRIORITY_RANKING'])
 */
export function getPlanFeatures(plan?: string | null, addons?: string[] | null): PlanFeatures {
  const base = { ...PLAN_FEATURES[normalizePlanId(plan)] };
  if (!addons || addons.length === 0) return base;

  // Addon-Overrides anwenden
  for (const addon of addons) {
    if (KNOWN_ADDONS.has(addon)) {
      const overrides = ADDON_FEATURE_OVERRIDES[addon as AddonKey];
      Object.assign(base, overrides);
    }
  }
  return base;
}

/** Prüft ob ein User das PRIORITY_RANKING Addon hat */
export function hasPriorityRanking(addons?: string[] | null): boolean {
  return !!addons && addons.includes('PRIORITY_RANKING');
}

/** Prüft ob ein User das APPOINTMENT_LINK Addon hat */
export function hasAppointmentLink(addons?: string[] | null): boolean {
  return !!addons && addons.includes('APPOINTMENT_LINK');
}

/** Feature-Key Typ für canAccess */
export type PlanFeatureKey = keyof Omit<
  PlanFeatures,
  'id' | 'label' | 'priceMonthly' | 'priceYearly' | 'videoType' | 'maxVideoDuration' | 'maxTreatments'
>;

/** Prüfen ob ein Plan (mit optionalen Addons) Zugriff auf ein bestimmtes Feature hat */
export function canAccess(plan: string | null | undefined, feature: PlanFeatureKey, addons?: string[] | null): boolean {
  const features = getPlanFeatures(plan, addons);
  return !!features[feature];
}

/** Alle verfügbaren Plans (für Pricing-Seite, Admin) */
export function getAllPlans(): PlanFeatures[] {
  return [PLAN_FEATURES.basis, PLAN_FEATURES.standard, PLAN_FEATURES.premium];
}

/** Plan-Reihenfolge für Vergleich (free < standard < premium) */
export function planLevel(plan?: string | null): number {
  const id = normalizePlanId(plan);
  if (id === 'basis') return 0;
  if (id === 'standard') return 1;
  return 2;
}

/** Prüft ob planA >= planB ist */
export function isPlanAtLeast(planA: string | null | undefined, planB: PlanId): boolean {
  return planLevel(planA) >= planLevel(planB);
}
